import Image from 'next/image';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';
import { CONNECTOR_ICONS } from '@/components/Chat/connectors/icons';

import { useEmployeesT } from './kit';

const LOGOS = 'https://media.easy-peasy.ai/landing/ai-employees/v1/apps/';

// Apps whose marks ship with the product, then ones served from our CDN.
// Brand names are never translated, except Google's, which has its own
// name for Calendar and Drive in each language (`localName`).
const APPS: {
  name: string;
  icon?: string;
  logo?: string;
  localName?: boolean;
}[] = [
  { name: 'Gmail', icon: 'gmail' },
  { name: 'Google Calendar', icon: 'google-calendar', localName: true },
  { name: 'Google Drive', icon: 'google-drive', localName: true },
  { name: 'Outlook', icon: 'outlook-mail' },
  { name: 'OneDrive', icon: 'onedrive' },
  { name: 'OneNote', icon: 'onenote' },
  { name: 'Notion', icon: 'notion' },
  { name: 'GitHub', icon: 'github' },
  { name: 'Canva', icon: 'canva' },
  { name: 'Stripe', logo: 'stripe' },
  { name: 'PayPal', logo: 'paypal' },
  { name: 'PostHog', icon: 'posthog' },
  { name: 'Mixpanel', logo: 'mixpanel' },
  { name: 'Supabase', icon: 'supabase' },
  { name: 'Crisp', icon: 'crisp' },
  { name: 'Klaviyo', logo: 'klaviyo' },
  { name: 'Trello', logo: 'trello' },
  { name: 'Asana', logo: 'asana' },
  { name: 'Linear', logo: 'linear' },
  { name: 'monday.com', logo: 'monday' },
  { name: 'Airtable', logo: 'airtable' },
  { name: 'ClickUp', logo: 'clickup' },
  { name: 'Cal.com', logo: 'calcom' },
  { name: 'Zapier', logo: 'zapier' },
  { name: 'Semrush', logo: 'semrush' },
  { name: 'Ahrefs', logo: 'ahrefs' },
  { name: 'Meta Ads', logo: 'meta-ads' },
  { name: 'Webflow', logo: 'webflow' },
  { name: 'Sentry', logo: 'sentry' },
];

/** The apps wall: what an employee can be connected to, one by one. */
export default function EmployeesApps({ appTotal }: { appTotal: number }) {
  const { t } = useEmployeesT();
  const { ref, state } = useReveal<HTMLElement>(0.15);

  const points = [
    t('Several Gmail accounts? Pick the one each employee uses.'),
    t('Tell each employee how to use an app, like “reports only, no refunds”.'),
    t('Bring your own tools with any MCP server or API.'),
  ];

  return (
    <section
      id='apps'
      ref={ref}
      data-reveal={state}
      className={clsxm(styles.reveal, 'scroll-mt-8 py-20 sm:py-28')}
    >
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className={clsxm(styles.rv, 'lg:col-span-4')}>
          <SectionHeading
            size='md'
            title={t('Connect the apps they need.')}
            tail={t('And only those.')}
            lead={t(
              'Each employee works with the apps you give it: the support inbox for your support rep, Canva for your designer, Stripe and PostHog for your analyst.',
            )}
          />
          <ul className='mt-8 space-y-3.5'>
            {points.map((point) => (
              <li
                key={point}
                className={clsxm('flex gap-3 text-[15px] leading-6', tone.body)}
              >
                <span className='mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-50 text-primary-700 dark:bg-primary-400/15 dark:text-primary-300'>
                  <CheckGlyph className='h-3 w-3' />
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>

        <div className='lg:col-span-8'>
          <ul className='grid grid-cols-4 gap-x-2 gap-y-5 sm:grid-cols-6 lg:grid-cols-6 xl:grid-cols-8'>
            {APPS.map((app, index) => (
              <li
                key={app.name}
                className={clsxm(styles.rv, 'flex flex-col items-center gap-2')}
                style={delay(Math.min(index, 16) * 25)}
              >
                <span
                  className={clsxm(
                    'flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-[0_8px_20px_-14px_rgba(22,12,90,0.45)] dark:bg-white',
                    tone.line,
                  )}
                >
                  {app.icon ? (
                    CONNECTOR_ICONS[app.icon]?.('h-7 w-7')
                  ) : (
                    <Image
                      src={`${LOGOS}${app.logo}.webp`}
                      alt=''
                      width={28}
                      height={28}
                      unoptimized
                      className='h-7 w-7 rounded-md object-contain'
                    />
                  )}
                </span>
                <span
                  className={clsxm(
                    'line-clamp-2 max-w-full text-center text-xs font-medium leading-tight',
                    tone.body,
                  )}
                >
                  {app.localName ? t(app.name) : app.name}
                </span>
              </li>
            ))}
            {appTotal > APPS.length ? (
              <li
                className={clsxm(styles.rv, 'flex flex-col items-center gap-2')}
                style={delay(16 * 25)}
              >
                <span className='flex h-14 w-14 items-center justify-center rounded-2xl border border-dashed border-[#0f0f28]/25 text-[15px] font-semibold tabular-nums text-[#56566d] dark:border-white/20 dark:text-[#a4a4bd]'>
                  +{appTotal - APPS.length}
                </span>
                <span
                  className={clsxm(
                    'line-clamp-2 max-w-full text-center text-xs font-medium leading-tight',
                    tone.body,
                  )}
                >
                  {t('More apps')}
                </span>
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </section>
  );
}
